/**
 * Foreign Tax Residency Component
 *
 * This component handles the collection of foreign tax residency information,
 * which is required for CRS (Common Reporting Standard) compliance.
 * It determines if a person has tax residency in a country other than their
 * country of residence and collects the relevant country and tax number.
 */

import { useState, useCallback } from 'react';
import type { ReactElement } from 'react';
import {
  RadioGroupBoolean,
  RadioButtonBoolean,
  TextField,
  Select,
  Option,
  Flag,
  DEFAULT_COUNTRY_CODES,
  type CountryCode,
} from '@repo/design-system-kit';
import { useIntl } from 'react-intl';
import cx from 'clsx';
import styles from './tax-residency.module.css';

const LABEL_ID = 'foreign-tax-residency-label';

export interface ForeignTaxResidencyData {
  isTaxResident?: boolean | null;
  taxNumber?: string;
  country?: CountryCode;
}

export interface ForeignTaxResidencyProps {
  className?: string;
  value?: ForeignTaxResidencyData;
  initialData?: Partial<ForeignTaxResidencyData>;
  onChange: (data: ForeignTaxResidencyData) => void;
  isRequired?: boolean;
  label: string;
  showErrors?: boolean;
  blacklistedCountries?: CountryCode[];
}

export function ForeignTaxResidency({
  className,
  value,
  initialData,
  onChange,
  label,
  isRequired = false,
  showErrors = false,
  blacklistedCountries,
}: ForeignTaxResidencyProps): ReactElement {
  const { formatMessage, formatDisplayName } = useIntl();
  const [internalData, setInternalData] = useState<ForeignTaxResidencyData>(() => ({
    ...initialData,
  }));

  // Use value prop if provided (controlled), otherwise use internal state (uncontrolled)
  const formData = value !== undefined ? value : internalData;
  const showConditionalFields = formData.isTaxResident;

  const updateFormData = useCallback(
    (updates: Partial<ForeignTaxResidencyData>): void => {
      const newData: ForeignTaxResidencyData = { ...formData, ...updates };
      // Update internal state only if not controlled
      if (value === undefined) {
        setInternalData(newData);
      }
      onChange(newData);
    },
    [formData, onChange, value]
  );

  const handleTaxResidencyChange = useCallback(
    (isTaxResident: boolean): void => {
      const updates: Partial<ForeignTaxResidencyData> = { isTaxResident };

      if (!isTaxResident) {
        updates.taxNumber = undefined;
        updates.country = undefined;
      }

      updateFormData(updates);
    },
    [updateFormData]
  );

  const handleTaxNumberChange = useCallback(
    (taxNumber: string): void => {
      updateFormData({ taxNumber: taxNumber.trim() || undefined });
    },
    [updateFormData]
  );

  const handleCountryChange = useCallback(
    (country: CountryCode): void => {
      updateFormData({ country });
    },
    [updateFormData]
  );

  return (
    <div className={cx(styles.wrapper, className)} data-test-foreign-tax-residency>
      <div className={styles.sectionWrapper}>
        <h2 className="body-standard" id={LABEL_ID}>
          {label}
        </h2>
      </div>

      <div className={styles.sectionWrapper}>
        <RadioGroupBoolean
          aria-labelledby={LABEL_ID}
          errorMessage={
            showErrors && isRequired && typeof formData.isTaxResident !== 'boolean'
              ? formatMessage({
                  id: 'customer-registration-kit.generic.required-field.error',
                })
              : undefined
          }
          isInvalid={Boolean(
            showErrors && isRequired && typeof formData.isTaxResident !== 'boolean'
          )}
          isRequired={isRequired}
          onChange={handleTaxResidencyChange}
          value={formData.isTaxResident ?? undefined}
        >
          <RadioButtonBoolean data-test-foreign-tax-residency-yes value>
            {formatMessage({ id: 'customer-registration-kit.common.yes' })}
          </RadioButtonBoolean>
          <RadioButtonBoolean data-test-foreign-tax-residency-no value={false}>
            {formatMessage({ id: 'customer-registration-kit.common.no' })}
          </RadioButtonBoolean>
        </RadioGroupBoolean>
      </div>

      {showConditionalFields ? (
        <div className={styles.conditionalFields}>
          <Select
            data-test-foreign-tax-residency-country
            errorMessage={
              showErrors && isRequired && !formData.country
                ? formatMessage({
                    id: 'customer-registration-kit.generic.required-field.error',
                  })
                : undefined
            }
            isRequired
            label={formatMessage({
              id: 'customer-registration-kit.tax-residency-country.label',
            })}
            onSelectionChange={key => {
              handleCountryChange(key as CountryCode);
            }}
            placeholder={formatMessage({
              id: 'customer-registration-kit.tax-residency-country.placeholder',
            })}
            selectedKey={formData.country}
            withSearch={{
              placeholder: formatMessage({
                id: 'customer-registration-kit.placeholders.search-copy',
              }),
              emptyMessage: formatMessage({
                id: 'customer-registration-kit.generic.no-results.error_copy',
              }),
            }}
          >
            {DEFAULT_COUNTRY_CODES.filter(c => !blacklistedCountries?.includes(c)).map(
              countryCode => {
                const countryName =
                  formatDisplayName(countryCode, { type: 'region' }) ?? countryCode;
                return (
                  <Option id={countryCode} key={countryCode} textValue={countryName}>
                    <div className={styles.countryOption}>
                      <Flag code={countryCode} width={20} />
                      <span className="body-standard">{countryName}</span>
                    </div>
                  </Option>
                );
              }
            )}
          </Select>

          <TextField
            data-test-foreign-tax-number
            errorMessage={
              showErrors && isRequired && !formData.taxNumber?.trim()
                ? formatMessage({
                    id: 'customer-registration-kit.generic.required-field.error',
                  })
                : undefined
            }
            isRequired
            label={formatMessage({
              id: 'customer-registration-kit.tax-residency-country.foreign-tin',
            })}
            onChange={handleTaxNumberChange}
            placeholder={formatMessage({
              id: 'customer-registration-kit.foreign-tin.placeholder',
            })}
            value={formData.taxNumber ?? ''}
          />
        </div>
      ) : null}
    </div>
  );
}
